<template>
  <div class="gpt-image-seo-content">
    <section class="gpt-section">
      <div class="gpt-heading">
        <span>{{ text('sectionKicker') }}</span>
        <h2>{{ $t('brandSeo.sectionTitle', { title: text('keyFeaturesTitle') }) }}</h2>
        <p>{{ text('keyFeaturesIntro') }}</p>
      </div>
      <div class="gpt-feature-grid">
        <article v-for="(feature, idx) in features" :key="idx">
          <span>0{{ idx + 1 }}</span>
          <h3>{{ feature.name }}</h3>
          <p>{{ feature.short }}</p>
        </article>
      </div>
    </section>

    <section class="gpt-section gpt-showcase">
      <div class="gpt-heading gpt-heading--center">
        <span>{{ text('showcaseKicker') }}</span>
        <h2>{{ text('showcaseTitle') }}</h2>
        <p>{{ text('showcaseIntro') }}</p>
      </div>
      <article class="gpt-showcase-row" v-for="(feature, idx) in features" :key="'showcase-' + idx">
        <div class="gpt-showcase-copy">
          <span>{{ idx + 1 }}</span>
          <h3>{{ feature.name }}</h3>
          <p>{{ feature.desc }}</p>
        </div>
        <div
          class="gpt-example-list"
          :class="{ 'gpt-example-list--single': featureExamples(feature).length === 1 }"
          v-if="featureExamples(feature).length"
        >
          <div class="gpt-example-head" v-if="featureExamples(feature).length === 1">
            <span>{{ $t('seo.imageModels.demoPrompt') }}</span>
            <span>{{ $t('seo.imageModels.imagePlaceholder') }}</span>
          </div>
          <div class="gpt-example-pair" v-for="(example, exampleIdx) in featureExamples(feature)" :key="exampleIdx">
            <div class="gpt-example-card">
              <span class="gpt-example-label">{{ $t('seo.imageModels.demoPrompt') }}</span>
              <p>“{{ example.prompt }}”</p>
              <button type="button" @click="$emit('use-prompt', example.prompt)">{{ $t('seo.imageModels.usePrompt') }}</button>
            </div>
            <figure class="gpt-example-visual">
              <img v-if="example.image" :src="example.image" :alt="example.alt || feature.name + ' example'" loading="lazy">
              <div class="gpt-image-placeholder" v-else>
                <span>{{ $t('seo.imageModels.imagePlaceholder') }}</span>
              </div>
            </figure>
          </div>
        </div>
      </article>
    </section>

    <section class="gpt-section" v-if="variants.length">
      <div class="gpt-heading">
        <span>{{ text('variantsKicker') }}</span>
        <h2>{{ text('variantsTitle') }}</h2>
        <p>{{ text('variantsIntro') }}</p>
      </div>
      <div class="gpt-variant-grid">
        <article v-for="variant in variants" :key="variant.name">
          <div><h3>{{ variant.name }}</h3><span>{{ variant.tag }}</span></div>
          <p>{{ variant.desc }}</p>
          <strong>{{ variant.bestFor }}</strong>
        </article>
      </div>
    </section>

    <section class="gpt-section gpt-use-cases">
      <div class="gpt-heading gpt-heading--center">
        <span>{{ text('useCasesKicker') }}</span>
        <h2>{{ text('useCasesTitle') }}</h2>
      </div>
      <div class="gpt-use-case-grid">
        <article v-for="item in useCases" :key="item.name">
          <span></span>
          <h3>{{ item.name }}</h3>
          <p>{{ item.desc }}</p>
        </article>
      </div>
    </section>

    <section class="gpt-section gpt-faq-section">
      <div class="gpt-heading">
        <span>FAQ</span>
        <h2>{{ $t('brandSeo.faqTitle', { title: text('faqTitle') }) }}</h2>
      </div>
      <div class="gpt-faqs">
        <details v-for="(faq, idx) in faqs" :key="idx">
          <summary><span>{{ faq.q }}</span><i></i></summary>
          <p>{{ faq.a }}</p>
        </details>
      </div>
    </section>

    <section class="gpt-section gpt-how-to">
      <div class="gpt-how-to-heading">
        <span>{{ text('howToKicker') }}</span>
        <h2>{{ $t('brandSeo.sectionTitle', { title: text('howToTitle') }) }}</h2>
        <p>{{ text('howToIntro') }}</p>
      </div>
      <div class="gpt-how-to-grid">
        <article v-for="(step, idx) in howToSteps" :key="idx">
          <strong>0{{ idx + 1 }}</strong>
          <div><h3>{{ step.title }}</h3><p>{{ step.desc }}</p></div>
        </article>
      </div>
      <button type="button" @click="$emit('back-to-generator')">{{ text('howToCta') }}</button>
    </section>

    <section class="gpt-section gpt-related-section">
      <h2>{{ $t('seo.imageModels.relatedModelsTitle') }}</h2>
      <div class="gpt-related">
        <router-link v-for="model in relatedModels" :key="model.id" :to="modelLink(model)">
          <span>{{ model.name }}</span><i>&rarr;</i>
        </router-link>
      </div>
    </section>
  </div>
</template>

<script>
module.exports = {
  name: 'GptImageSeoContent',
  props: {
    features: { type: Array, default: function () { return [] } },
    variants: { type: Array, default: function () { return [] } },
    useCases: { type: Array, default: function () { return [] } },
    faqs: { type: Array, default: function () { return [] } },
    howToSteps: { type: Array, default: function () { return [] } },
    relatedModels: { type: Array, default: function () { return [] } }
  },
  methods: {
    // 读取 GPT Image SEO 文案，保持组件仅依赖当前模型的翻译节点。
    text: function (field) {
      return this.$t('seo.imageModels.gptImage.' + field)
    },
    // 将功能配置统一转换为提示词与图片案例数组。
    featureExamples: function (feature) {
      if (feature.demo && feature.demo.rows && feature.demo.rows.length) return feature.demo.rows
      if (feature.prompt) return [{ prompt: feature.prompt, image: feature.image || '', alt: feature.alt || '' }]
      return []
    },
    // 构建保留当前语言前缀的相关模型链接。
    modelLink: function (model) {
      var lang = this.$route.params.lang
      var routeId = model.routeId || model.id
      return (lang ? '/' + lang : '') + '/models/' + routeId
    }
  }
}
</script>

<style scoped>
.gpt-image-seo-content { color: #17191f; }
.gpt-section { margin-bottom: 100px; }
.gpt-heading { max-width: 700px; margin-bottom: 34px; }
.gpt-heading--center { margin-right: auto; margin-left: auto; text-align: center; }
.gpt-heading > span { display: inline-block; color: #059669; font-size: 12px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; }
.gpt-heading h2, .gpt-related-section > h2 { margin: 9px 0 10px; font-size: clamp(28px, 3vw, 38px); line-height: 1.2; letter-spacing: -.03em; }
.gpt-heading p { margin: 0; color: #727782; font-size: 15px; line-height: 1.7; }
.gpt-feature-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; }
.gpt-feature-grid article { min-height: 126px; padding: 24px; border: 1px solid #e9eaed; border-radius: 16px; background: #fff; }
.gpt-feature-grid article:first-child { display: grid; grid-column: 1 / -1; grid-template-columns: 70px minmax(180px, .62fr) minmax(260px, 1.38fr); align-items: center; gap: 22px; }
.gpt-feature-grid article > span { color: #0a9b70; font-size: 11px; font-weight: 800; letter-spacing: .1em; }
.gpt-feature-grid h3 { margin: 30px 0 8px; font-size: 17px; }
.gpt-feature-grid article:first-child h3 { margin: 0; }
.gpt-feature-grid p { margin: 0; color: #747984; font-size: 13px; line-height: 1.65; }
.gpt-showcase { padding: 68px 64px; border-radius: 28px; background: #f7f8f9; }
.gpt-showcase-row { padding: 54px 0; border-top: 1px solid #e2e4e7; }
.gpt-showcase-copy { max-width: 790px; margin-bottom: 26px; }
.gpt-showcase-copy > span { color: #059669; font-size: 12px; font-weight: 800; }
.gpt-showcase-copy h3 { margin: 8px 0 12px; font-size: 24px; }
.gpt-showcase-copy p { margin: 0; color: #676d78; font-size: 14px; line-height: 1.8; }
.gpt-example-list { display: grid; width: 100%; max-width: 900px; margin: 0 auto; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
.gpt-example-pair { display: grid; overflow: hidden; grid-template-columns: 1fr; border: 1px solid #e3e5e8; border-radius: 18px; background: #fff; }
.gpt-example-card { display: flex; min-height: 150px; padding: 22px; flex-direction: column; background: #fff; }
.gpt-example-label { color: #9a9fa8; font-size: 10px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; }
.gpt-example-card p { margin: 12px 0 18px; color: #3f444e; font-size: 13px; line-height: 1.7; }
.gpt-example-card button { align-self: flex-start; margin-top: auto; padding: 0; border: 0; background: transparent; color: #059669; cursor: pointer; font-size: 12px; font-weight: 700; }
.gpt-example-visual { order: -1; height: 280px; min-height: 0; margin: 0; overflow: hidden; border-bottom: 1px solid #e8eaed; background: #f0f2f3; }
.gpt-example-visual img { display: block; width: 100%; height: 100%; object-fit: cover; }
.gpt-image-placeholder { display: flex; height: 100%; align-items: center; justify-content: center; color: #9ba1aa; }
.gpt-example-list--single { display: block; max-width: 820px; overflow: hidden; border: 1px solid #e3e5e8; border-radius: 14px; }
.gpt-example-head { display: grid; grid-template-columns: repeat(2, 1fr); background: #f7f8f9; border-bottom: 1px solid #e3e5e8; }
.gpt-example-head span { padding: 16px 20px; font-size: 12px; font-weight: 700; }
.gpt-example-head span + span { border-left: 1px solid #e3e5e8; }
.gpt-example-list--single .gpt-example-pair { grid-template-columns: repeat(2, 1fr); border: 0; border-radius: 0; }
.gpt-example-list--single .gpt-example-card { min-height: 300px; justify-content: center; }
.gpt-example-list--single .gpt-example-visual { order: initial; height: 300px; border-bottom: 0; border-left: 1px solid #e8eaed; }
.gpt-variant-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 14px; }
.gpt-variant-grid article { padding: 30px; border: 1px solid #e7e9ec; border-radius: 15px; }
.gpt-variant-grid article > div { display: flex; align-items: center; justify-content: space-between; }
.gpt-variant-grid h3 { margin: 0; font-size: 18px; }
.gpt-variant-grid article > div span { padding: 5px 8px; border-radius: 6px; background: #f2f4f5; color: #777d87; font-size: 10px; font-weight: 700; text-transform: uppercase; }
.gpt-variant-grid p { min-height: 45px; margin: 14px 0; color: #737984; font-size: 13px; line-height: 1.65; }
.gpt-variant-grid strong { color: #047857; font-size: 12px; }
.gpt-use-cases { padding: 70px 60px; border: 1px solid #e8eaed; border-radius: 26px; }
.gpt-use-case-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.gpt-use-case-grid article { display: grid; grid-template-columns: 18px 1fr; padding: 22px; border: 1px solid #e8eaed; border-radius: 14px; }
.gpt-use-case-grid article > span { width: 8px; height: 8px; grid-row: 1 / 3; margin-top: 5px; border-radius: 50%; background: #12a87b; box-shadow: 0 0 0 6px #eaf8f3; }
.gpt-use-case-grid h3 { margin: 0 0 7px; font-size: 16px; }
.gpt-use-case-grid p { grid-column: 2; margin: 0; color: #777c86; font-size: 13px; line-height: 1.65; }
.gpt-faq-section { max-width: 860px; margin-right: auto; margin-left: auto; }
.gpt-faqs { border-top: 1px solid #e6e8eb; }
.gpt-faqs details { border-bottom: 1px solid #e6e8eb; }
.gpt-faqs summary { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 22px 2px; cursor: pointer; font-size: 15px; font-weight: 650; list-style: none; }
.gpt-faqs summary::-webkit-details-marker { display: none; }
.gpt-faqs summary i { position: relative; width: 14px; height: 14px; flex: 0 0 14px; }
.gpt-faqs summary i::before, .gpt-faqs summary i::after { content: ''; position: absolute; top: 6px; left: 1px; width: 12px; height: 1px; background: #707681; }
.gpt-faqs summary i::after { transform: rotate(90deg); }
.gpt-faqs details[open] summary i::after { transform: rotate(0); }
.gpt-faqs details p { margin: -4px 0 22px; color: #6f7580; font-size: 14px; line-height: 1.8; }
.gpt-how-to { position: relative; padding: 72px 58px; border-radius: 28px; background: #15181d; color: #fff; }
.gpt-how-to-heading { max-width: 720px; margin: 0 auto 42px; text-align: center; }
.gpt-how-to-heading > span { color: #6ee7b7; font-size: 11px; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; }
.gpt-how-to-heading h2 { margin: 12px 0 10px; color: #fff; font-size: clamp(28px, 3.5vw, 40px); }
.gpt-how-to-heading p { margin: 0; color: #aeb4bd; font-size: 14px; line-height: 1.7; }
.gpt-how-to-grid { display: grid; max-width: 820px; margin: 0 auto; gap: 16px; }
.gpt-how-to-grid article { display: grid; grid-template-columns: 72px 1fr; align-items: stretch; gap: 14px; }
.gpt-how-to-grid article > strong { align-self: center; color: #fff; font-size: 38px; }
.gpt-how-to-grid article > div { padding: 24px; border: 1px solid rgba(255,255,255,.07); border-radius: 15px; background: rgba(8,10,14,.72); }
.gpt-how-to-grid h3 { margin: 0 0 10px; color: #fff; font-size: 16px; }
.gpt-how-to-grid p { margin: 0; color: #b5bac2; font-size: 13px; line-height: 1.7; }
.gpt-how-to > button { display: block; margin: 34px auto 0; padding: 13px 28px; border: 1px solid rgba(255,255,255,.22); border-radius: 999px; background: #fff; color: #17191f; cursor: pointer; font-size: 13px; font-weight: 800; }
.gpt-related-section { margin-bottom: 0; }
.gpt-related-section > h2 { font-size: 25px; }
.gpt-related { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.gpt-related a { display: flex; align-items: center; justify-content: space-between; padding: 18px; border: 1px solid #e8eaed; border-radius: 12px; color: #20232a; text-decoration: none; }
.gpt-related a span { font-size: 13px; font-weight: 700; }
.gpt-related a i { color: #059669; font-style: normal; }
@media (max-width: 900px) {
  .gpt-feature-grid article:first-child { grid-template-columns: 52px 1fr; }
  .gpt-feature-grid article:first-child p { grid-column: 2; }
  .gpt-showcase { padding: 52px 32px; }
}
@media (max-width: 640px) {
  .gpt-section { margin-bottom: 72px; }
  .gpt-feature-grid, .gpt-example-list, .gpt-variant-grid, .gpt-use-case-grid, .gpt-related { grid-template-columns: 1fr; }
  .gpt-feature-grid article:first-child { display: block; grid-column: auto; }
  .gpt-feature-grid article:first-child h3 { margin: 30px 0 8px; }
  .gpt-showcase { margin-right: -18px; margin-left: -18px; padding: 44px 18px; border-radius: 0; }
  .gpt-example-visual { height: 240px; }
  .gpt-example-list--single { border: 0; border-radius: 0; }
  .gpt-example-list--single .gpt-example-head { display: none; }
  .gpt-example-list--single .gpt-example-pair { grid-template-columns: 1fr; border: 1px solid #e3e5e8; border-radius: 14px; }
  .gpt-example-list--single .gpt-example-card { min-height: 0; }
  .gpt-example-list--single .gpt-example-visual { height: 240px; border-top: 1px solid #e8eaed; border-left: 0; }
  .gpt-use-cases { padding: 46px 24px; }
  .gpt-how-to { margin-right: -18px; margin-left: -18px; padding: 52px 18px; border-radius: 0; }
  .gpt-how-to-grid article { grid-template-columns: 1fr; }
}
</style>
